import {
  getSessionData,
  getVersionedCallbackUrl,
  redirectToWebform,
} from "@repo/shared-utils/common-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  deleteFromCartApi,
  getCart,
  solarInsert,
  solarRead,
  type ICartItemDetails,
  type ISolarInsertApiRequest,
} from "@repo/connection-manager";
import type { SSORedirectHandler } from "../types/SSO.types";
import { PDP_DETAILS, WHEELS_OFFER } from "../../../api-client/src/constants/util";
import { SOLAR_APPLICATION_ERROR_EVENT } from "@repo/sso/constants";

type SolarJourneyName = "SRL_NTB" | "SRL_ETB" | "SUL_NTB" | "SUL_ETB" | "SUCL_NTB" | "SUCL_ETB";
type SolarProdCode = "SRL" | "SUL" | "SUCL";

const PRODUCT_CODE_PREFIXES: SolarProdCode[] = ["SRL", "SUL", "SUCL"];
const PRODUCT_CODE_EXACT_MATCHES = ["SOLAR_FINANCE"];
const DEFAULT_JOURNEY_NAME: SolarJourneyName = "SRL_NTB";
const WHEELS_CONVERT = "WHEELS_CONVERT";

type SolarInsertData = Partial<ISolarInsertApiRequest> & {
  id?: string;
};

type ResumeJourneyData =
  | {
      type: "resume";
      data: {
        appId?: string;
        prod?: string;
        eventCode?: string;
      };
    }
  | {
      type: "new";
    }
  | {
      type: "dismiss";
    };

type ResumeJourneyResolution = {
  applicationId?: string;
  dismissed?: boolean;
};

type ResumeJourneyModalHandler = (cartData: ICartItemDetails[]) => Promise<ResumeJourneyData>;
type DedupeCartModalHandler = () => void;

const getResumeJourneyModalHandler = (
  data: Parameters<SSORedirectHandler>[0]
): ResumeJourneyModalHandler | undefined => {
  const openResumeJourneyModal = (data.data as { openResumeJourneyModal?: unknown } | undefined)
    ?.openResumeJourneyModal;

  return typeof openResumeJourneyModal === "function"
    ? (openResumeJourneyModal as ResumeJourneyModalHandler)
    : undefined;
};

const getDedupeCartModalHandler = (
  data: Parameters<SSORedirectHandler>[0]
): DedupeCartModalHandler | undefined => {
  const openDedupeCartModal = (data.data as { openDedupeCartModal?: unknown } | undefined)
    ?.openDedupeCartModal;

  return typeof openDedupeCartModal === "function"
    ? (openDedupeCartModal as DedupeCartModalHandler)
    : undefined;
};

const SOLAR_HANDLER_LOG = "[SolarSSO][Handler]";

const getSolarCartDetails = async (): Promise<ICartItemDetails[]> => {
  const cart = await getCart();
  return (cart?.data?.cartDetails ?? []).filter((item) => {
    const prodCode = item.prod?.toUpperCase();
    return (
      PRODUCT_CODE_EXACT_MATCHES.includes(prodCode) ||
      PRODUCT_CODE_PREFIXES.some((prefix) => prodCode?.startsWith(prefix))
    );
  });
};

const resolveResumeJourneyFromCart = async (
  data: Parameters<SSORedirectHandler>[0],
  mobileNo: string,
  cartDetails: ICartItemDetails[],
  existingApplicationId?: string
): Promise<ResumeJourneyResolution> => {
  if (!data.checkCart || !data.showResumePopup) {
    return { applicationId: existingApplicationId };
  }

  const openResumeJourneyModal = getResumeJourneyModalHandler(data);
  if (!openResumeJourneyModal) {
    return { applicationId: existingApplicationId };
  }

  if (!cartDetails.length) {
    return { applicationId: existingApplicationId };
  }

  const journey = await openResumeJourneyModal(cartDetails);
  if (journey.type === "resume") {
    return {
      applicationId: journey.data.appId,
    };
  }

  if (journey.type === "dismiss") {
    return {
      applicationId: existingApplicationId,
      dismissed: true,
    };
  }

  const cartAppId = cartDetails[0]?.appId;
  if (!cartAppId) {
    return { applicationId: existingApplicationId };
  }

  await deleteFromCartApi({
    appId: cartAppId,
    mobileNo,
    channel: "3in1",
  });

  return { applicationId: existingApplicationId };
};

export const solarHandler: SSORedirectHandler = async (data) => {
  const { mobileNo, custType } = await getSessionData();
  const solarInsertData = (data.data?.solarInsert ?? {}) as SolarInsertData;

  const cartDetails = await getSolarCartDetails();
  const cartAppId = cartDetails[0]?.appId;

  if (cartAppId) {
    console.info(`${SOLAR_HANDLER_LOG} checking existing cart application status`, {
      cartAppId,
    });

    try {
      const cartReadResponse = await solarRead({
        id: cartAppId,
        mobileNo,
      });
      const cartLoan = cartReadResponse?.solarLoanDetailsList?.[0] as
        { eventCode?: string } | undefined;

      if (cartLoan?.eventCode === WHEELS_CONVERT) {
        console.info(
          `${SOLAR_HANDLER_LOG} cart application already converted - opening visit cart drawer`,
          { cartAppId }
        );

        const openDedupeCartModal = getDedupeCartModalHandler(data);
        if (openDedupeCartModal) {
          openDedupeCartModal();
        } else {
          console.warn(
            `${SOLAR_HANDLER_LOG} openDedupeCartModal handler not provided - unable to show drawer`
          );
        }

        console.info(`${SOLAR_HANDLER_LOG} complete (dedupe drawer shown, no redirect)`);
        return;
      }
    } catch (error) {
      console.warn(`${SOLAR_HANDLER_LOG} cart application status check failed`, {
        cartAppId,
        error,
      });
      // Fall through to the normal resume-popup flow rather than blocking the CTA.
    }
  }

  const { applicationId: applicationID, dismissed } = await resolveResumeJourneyFromCart(
    data,
    mobileNo,
    cartDetails,
    solarInsertData.id
  );

  if (dismissed) {
    console.info(`${SOLAR_HANDLER_LOG} resume popup dismissed - aborting redirect flow`);
    return;
  }

  console.info(`${SOLAR_HANDLER_LOG} start`, {
    hasApplicationId: Boolean(applicationID),
    mappingModule: data.mapping?.ssoModule,
    mobileNoPresent: Boolean(mobileNo),
    payload: solarInsertData,
  });

  let response: { id?: string; eventCode?: string } | null = null;
  let eventCode = WHEELS_OFFER;

  if (!applicationID) {
    console.info(`${SOLAR_HANDLER_LOG} calling solarInsert`, {
      journeyName: solarInsertData.journeyName ?? DEFAULT_JOURNEY_NAME,
      sourceSystem: isOpenInApp() ? "3in1App" : "3in1Web",
    });

    try {
      response = await solarInsert({
        ...solarInsertData,
        mobileNo,
        custType,
        journeyName: solarInsertData.journeyName ?? DEFAULT_JOURNEY_NAME,
        eventCode: WHEELS_OFFER,
        eventName: PDP_DETAILS,
        prodName: PRODUCT_CODE_PREFIXES[0],
        apiVersion: "P.11.1.4",
        srcChannel: "3in1",
        sourceSystem: isOpenInApp() ? "3in1App" : "3in1Web",
      });
    } catch (error) {
      console.error(`${SOLAR_HANDLER_LOG} solarInsert failed`, error);
      window.dispatchEvent(
        new CustomEvent(SOLAR_APPLICATION_ERROR_EVENT, { detail: { source: "solarHandler" } })
      );
      return;
    }

    console.info(`${SOLAR_HANDLER_LOG} solarInsert response`, {
      id: response?.id,
    });
  } else {
    console.info(`${SOLAR_HANDLER_LOG} resuming application - calling solarRead`, {
      applicationID,
    });

    const readResponse = await solarRead({
      id: applicationID,
      mobileNo,
    });

    const resumedLoan = readResponse?.solarLoanDetailsList?.[0] as
      { eventCode?: string } | undefined;
    eventCode = resumedLoan?.eventCode ?? WHEELS_OFFER;

    console.info(`${SOLAR_HANDLER_LOG} solarRead response`, {
      applicationID,
      eventCode,
    });
  }

  const finalApplicationId = response?.id ?? applicationID;

  const callbackUrl = await getVersionedCallbackUrl(data.mapping);

  console.info(`${SOLAR_HANDLER_LOG} redirectToWebform`, {
    callbackUrl: callbackUrl.toString(),
    applicationID: finalApplicationId,
    mobileNo,
    product: PRODUCT_CODE_PREFIXES[0],
  });

  await redirectToWebform(callbackUrl.toString(), {
    applicationID: finalApplicationId,
    mobileNo,
    product: PRODUCT_CODE_PREFIXES[0],
  });

  console.info(`${SOLAR_HANDLER_LOG} complete`);
};
